import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../App';
import { ConfirmModal } from '../components/Modals';
import { useIsMobile, versionCompare, isUpdateAvailable, isAheadOfChannel, unmetRequirements, formatDateTime, timeAgo } from '../utils';

// ─── helpers ────────────────────────────────────────────────────────────────

const CHANNEL_STYLE = {
  beta:       { bg: 'rgba(96,165,250,0.1)',  color: '#60a5fa', border: '#60a5fa' },
  stable:     { bg: 'rgba(74,222,128,0.1)',  color: '#4ade80', border: '#4ade80' },
  deprecated: { bg: 'rgba(156,163,175,0.1)', color: '#9ca3af', border: '#9ca3af' },
};

function formatEnvironment(env) {
  if (!env || (!env.python_version && !env.websockets_version)) return 'Desconocido';
  return `Python ${env.python_version || '?'} · websockets ${env.websockets_version || '?'}`;
}

// #183 — único punto que decide qué mostrar sobre un update pendiente, usado
// tanto por la tarjeta (mobile) como por la tabla (desktop): así los dos no
// pueden desincronizarse si mañana cambia el criterio en uno solo.
// No toca la condición de "hay algo pendiente" (versionCompare), sólo agrega
// el detalle de si el último intento reportado falló.
export function hasPendingUpdate(server) {
  return !!(server.pending_version && server.agent_version &&
    versionCompare(server.pending_version, server.agent_version) > 0);
}

export function PendingUpdateInfo({ server, compact }) {
  if (!hasPendingUpdate(server)) return null;

  const failed = server.last_update_result_ok === false;

  if (compact) {
    return (
      <>
        {server.pending_version}
        {failed && (
          <div
            style={{
              color: 'var(--yellow)', marginTop: 2, maxWidth: 160,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
            title={`${server.last_update_result_reason || 'sin detalle'} — ${formatDateTime(server.last_update_result_at)} (${timeAgo(server.last_update_result_at)})`}
          >
            ⚠ {server.last_update_result_reason || 'sin detalle'}
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
      Pendiente: {server.pending_version}
      {failed && (
        <div style={{ color: 'var(--yellow)', marginTop: 2 }}>
          ⚠ Último intento falló: {server.last_update_result_reason || 'sin detalle'}
          {' — '}
          <span title={timeAgo(server.last_update_result_at)}>{formatDateTime(server.last_update_result_at)}</span>
        </div>
      )}
    </div>
  );
}

function ChannelBadge({ channel }) {
  const s = CHANNEL_STYLE[channel] || CHANNEL_STYLE.deprecated;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px',
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase',
    }}>
      {channel}
    </span>
  );
}

function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      padding: '10px 18px', borderRadius: 4, fontSize: 13,
      background: type === 'ok' ? 'rgba(0,229,160,0.1)' : 'rgba(255,77,109,0.1)',
      border: `1px solid ${type === 'ok' ? 'var(--green)' : 'var(--red)'}`,
      color: type === 'ok' ? 'var(--green)' : 'var(--red)',
    }}>
      {type === 'ok' ? '✓' : '✕'} {msg}
    </div>
  );
}

// ─── ReleasesSection ────────────────────────────────────────────────────────

const DEPRECATED_VISIBLE = 3;

// #186 — por defecto: stable, beta y las DEPRECATED_VISIBLE deprecated más
// recientes (por released_at). Conserva el orden original de la lista.
export function splitReleases(releases) {
  const recentDeprecated = new Set(
    releases
      .filter(r => r.channel === 'deprecated')
      .sort((a, b) => new Date(b.released_at) - new Date(a.released_at))
      .slice(0, DEPRECATED_VISIBLE)
      .map(r => r.id)
  );
  const shown = releases.filter(r => r.channel !== 'deprecated' || recentDeprecated.has(r.id));
  return { shown, hiddenCount: releases.length - shown.length };
}

function OlderReleasesToggle({ hiddenCount, showAll, onToggle }) {
  if (hiddenCount === 0) return null;
  return (
    <button
      onClick={onToggle}
      style={{ marginTop: 8, padding: '6px 12px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)' }}
    >
      {showAll ? 'Ocultar versiones anteriores' : `Ver versiones anteriores (${hiddenCount})`}
    </button>
  );
}

export function ReleasesSection({ releases, servers, onChanged, showToast, isAdmin }) {
  const [promoting, setPromoting]       = useState(null);
  const [confirmPromote, setConfirmPromote] = useState(null);
  const [deprecating, setDeprecating]           = useState(null);
  const [confirmDeprecate, setConfirmDeprecate] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const isMobile = useIsMobile();
  const { shown, hiddenCount } = splitReleases(releases);
  const visibleReleases = showAll ? releases : shown;

  function nonCompliantServers(release) {
    if (!release.requirements) return [];
    return servers.filter(s => unmetRequirements(s.agent_environment, release.requirements).length > 0);
  }

  const confirmPromoteRelease = releases.find(r => r.version === confirmPromote);
  const confirmPromoteBad = confirmPromoteRelease ? nonCompliantServers(confirmPromoteRelease) : [];
  const confirmPromoteMessage = confirmPromoteBad.length > 0
    ? `${confirmPromoteBad.length} agente(s) no cumplen los requisitos de esta versión (${confirmPromoteBad.map(s => s.hostname).join(', ')}) y no la recibirán. La versión stable actual pasará a deprecated y los demás servers en canal stable comenzarán a actualizarse (grace period 24h). Esta acción no se puede deshacer.`
    : "La versión stable actual pasará a deprecated y los servers en canal stable comenzarán a actualizarse (grace period 24h). Esta acción no se puede deshacer.";

  async function doPromote(version) {
    setPromoting(version);
    try {
      const result = await api.promoteAgentRelease(version);
      const until = new Date(result.grace_period_until).toLocaleString();
      showToast(`Versión ${version} promovida a stable. Grace period hasta ${until}`, 'ok');
      onChanged();
    } catch (e) {
      showToast(`Error al promover: ${e.message}`, 'error');
    } finally {
      setPromoting(null);
      setConfirmPromote(null);
    }
  }

  async function doDeprecate(version) {
    setDeprecating(version);
    try {
      await api.deprecateAgentRelease(version);
      showToast(`Versión ${version} marcada como deprecated`, 'ok');
      onChanged();
    } catch (e) {
      showToast(`Error al deprecar: ${e.message}`, 'error');
    } finally {
      setDeprecating(null);
      setConfirmDeprecate(null);
    }
  }

  if (isMobile) {
    return (
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 16, marginBottom: 12 }}>
          Versiones disponibles ({releases.length})
        </h2>
        {visibleReleases.map(r => (
          <div key={r.id} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 4, padding: 12, marginBottom: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <strong style={{ fontFamily: 'var(--mono)' }}>{r.version}</strong>
              <ChannelBadge channel={r.channel} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
              Released: {new Date(r.released_at).toLocaleString()}
            </div>
            {r.promoted_at && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                Promovida: {new Date(r.promoted_at).toLocaleString()}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
              Servers: {r.servers_running}
            </div>
            {r.release_notes && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                {r.release_notes}
              </div>
            )}
            {r.channel === 'beta' && (() => {
              const bad = nonCompliantServers(r);
              return bad.length > 0 ? (
                <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 6 }}>
                  ⚠ {bad.length} agente{bad.length === 1 ? '' : 's'} no cumple{bad.length === 1 ? '' : 'n'} los requisitos:{' '}
                  {bad.map(s => s.hostname).join(', ')}
                </div>
              ) : null;
            })()}
            {r.channel === 'beta' && isAdmin && (
              <button
                onClick={() => setConfirmPromote(r.version)}
                disabled={promoting === r.version}
                style={{ width: '100%', padding: '8px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'var(--accent)', border: 'none', color: 'var(--bg)' }}
              >
                {promoting === r.version ? 'Promoviendo...' : 'Promover a stable'}
              </button>
            )}
            {r.channel === 'beta' && isAdmin && (
              <button
                onClick={() => setConfirmDeprecate(r.version)}
                disabled={deprecating === r.version}
                style={{ width: '100%', padding: '8px', marginTop: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)' }}
              >
                {deprecating === r.version ? 'Marcando...' : 'Marcar deprecated'}
              </button>
            )}
          </div>
        ))}
        <OlderReleasesToggle hiddenCount={hiddenCount} showAll={showAll} onToggle={() => setShowAll(v => !v)} />
        {confirmPromote && (
          <ConfirmModal
            title={`Promover ${confirmPromote} a stable`}
            message={confirmPromoteMessage}
            onConfirm={() => doPromote(confirmPromote)}
            onCancel={() => setConfirmPromote(null)}
          />
        )}
        {confirmDeprecate && (
          <ConfirmModal
            title={`Marcar ${confirmDeprecate} como deprecated`}
            message="Los agentes dejan de recibirla como candidata de actualización. Los que ya la tienen instalada NO se revierten. Esta acción no se puede deshacer desde el panel."
            onConfirm={() => doDeprecate(confirmDeprecate)}
            onCancel={() => setConfirmDeprecate(null)}
          />
        )}
      </section>
    );
  }

  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 16, marginBottom: 12 }}>
        Versiones disponibles ({releases.length})
      </h2>
      <div style={{ border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Versión</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Canal</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Released</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Promovida a stable</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Servers</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Release notes</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleReleases.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)' }}>
                  <strong>{r.version}</strong>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <ChannelBadge channel={r.channel} />
                </td>
                <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--muted)' }}>
                  {new Date(r.released_at).toLocaleString()}
                </td>
                <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--muted)' }}>
                  {r.promoted_at ? new Date(r.promoted_at).toLocaleString() : '—'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--mono)' }}>
                  {r.servers_running}
                </td>
                <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--muted)', maxWidth: 260 }}>
                  {r.release_notes || '—'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  {r.channel === 'beta' && (() => {
                    const bad = nonCompliantServers(r);
                    return bad.length > 0 ? (
                      <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 4, textAlign: 'right' }}>
                        ⚠ {bad.length} agente{bad.length === 1 ? '' : 's'} no cumple{bad.length === 1 ? '' : 'n'} los requisitos:{' '}
                        {bad.map(s => s.hostname).join(', ')}
                      </div>
                    ) : null;
                  })()}
                  {r.channel === 'beta' && isAdmin && (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setConfirmPromote(r.version)}
                        disabled={promoting === r.version}
                        style={{ fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'var(--accent)', border: 'none', color: 'var(--bg)', padding: '5px 10px' }}
                      >
                        {promoting === r.version ? 'Promoviendo...' : 'Promover a stable'}
                      </button>
                      <button
                        onClick={() => setConfirmDeprecate(r.version)}
                        disabled={deprecating === r.version}
                        style={{ fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)', padding: '5px 10px' }}
                      >
                        {deprecating === r.version ? 'Marcando...' : 'Marcar deprecated'}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OlderReleasesToggle hiddenCount={hiddenCount} showAll={showAll} onToggle={() => setShowAll(v => !v)} />

      {confirmPromote && (
        <ConfirmModal
          title={`Promover ${confirmPromote} a stable`}
          message={confirmPromoteMessage}
          onConfirm={() => doPromote(confirmPromote)}
          onCancel={() => setConfirmPromote(null)}
        />
      )}

      {confirmDeprecate && (
        <ConfirmModal
          title={`Marcar ${confirmDeprecate} como deprecated`}
          message="Los agentes dejan de recibirla como candidata de actualización. Los que ya la tienen instalada NO se revierten. Esta acción no se puede deshacer desde el panel."
          onConfirm={() => doDeprecate(confirmDeprecate)}
          onCancel={() => setConfirmDeprecate(null)}
        />
      )}
    </section>
  );
}

// ─── ServersUpdateSection ────────────────────────────────────────────────────

function ServersUpdateSection({
  servers, releases,
  filterChannel, setFilterChannel,
  filterClient, setFilterClient,
  filterUpdateAvail, setFilterUpdateAvail,
  onChanged, showToast, isAdmin,
}) {
  const [savingServer, setSavingServer]       = useState(null);
  const [triggeringServer, setTriggeringServer] = useState(null);
  const isMobile = useIsMobile();

  const uniqueClients = [...new Set(servers.map(s => s.client_name))].filter(Boolean).sort();

  const latestReleaseByChannel = {
    beta:   releases.find(r => r.channel === 'beta'),
    stable: releases.find(r => r.channel === 'stable'),
  };
  const latestByChannel = {
    beta:   latestReleaseByChannel.beta?.version,
    stable: latestReleaseByChannel.stable?.version,
  };

  function hasUpdateAvailable(server) {
    return isUpdateAvailable(server.agent_version, latestByChannel[server.channel]);
  }

  function aheadOfChannel(server) {
    return isAheadOfChannel(server.agent_version, latestByChannel[server.channel]);
  }

  function serverUnmetRequirements(server) {
    const release = latestReleaseByChannel[server.channel];
    if (!release || !release.requirements) return [];
    return unmetRequirements(server.agent_environment, release.requirements);
  }

  const filtered = servers.filter(s => {
    if (filterChannel !== 'all' && s.channel !== filterChannel) return false;
    if (filterClient !== 'all' && s.client_name !== filterClient) return false;
    if (filterUpdateAvail === 'available' && !hasUpdateAvailable(s)) return false;
    if (filterUpdateAvail === 'up-to-date' && hasUpdateAvailable(s)) return false;
    return true;
  });

  async function toggleChannel(server) {
    const newChannel = server.channel === 'stable' ? 'beta' : 'stable';
    setSavingServer(server.id);
    try {
      await api.updateServerConfig(server.id, { channel: newChannel });
      showToast(`${server.hostname}: canal cambiado a ${newChannel}`, 'ok');
      onChanged();
    } catch (e) {
      showToast(`Error: ${e.message}`, 'error');
    } finally {
      setSavingServer(null);
    }
  }

  async function toggleAutoUpdate(server) {
    setSavingServer(server.id);
    try {
      await api.updateServerConfig(server.id, { auto_update: !server.auto_update });
      showToast(`${server.hostname}: auto-update ${!server.auto_update ? 'activado' : 'desactivado'}`, 'ok');
      onChanged();
    } catch (e) {
      showToast(`Error: ${e.message}`, 'error');
    } finally {
      setSavingServer(null);
    }
  }

  async function triggerUpdate(server) {
    setTriggeringServer(server.id);
    try {
      const result = await api.triggerServerUpdate(server.id);
      showToast(`${server.hostname}: update a ${result.version} disparado`, 'ok');
      setTimeout(onChanged, 3000);
    } catch (e) {
      showToast(`Error: ${e.message}`, 'error');
    } finally {
      setTriggeringServer(null);
    }
  }

  const filterRow = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      <select
        value={filterChannel}
        onChange={e => setFilterChannel(e.target.value)}
        style={{ padding: '6px 10px', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)' }}
      >
        <option value="all">Todos los canales</option>
        <option value="stable">Stable</option>
        <option value="beta">Beta</option>
      </select>
      <select
        value={filterClient}
        onChange={e => setFilterClient(e.target.value)}
        style={{ padding: '6px 10px', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)' }}
      >
        <option value="all">Todos los clientes</option>
        {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select
        value={filterUpdateAvail}
        onChange={e => setFilterUpdateAvail(e.target.value)}
        style={{ padding: '6px 10px', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)' }}
      >
        <option value="all">Todos los estados</option>
        <option value="available">Update disponible</option>
        <option value="up-to-date">Al día</option>
      </select>
    </div>
  );

  if (isMobile) {
    return (
      <section>
        <h2 style={{ fontSize: 16, marginBottom: 12 }}>
          Servidores ({filtered.length} de {servers.length})
        </h2>
        {filterRow}
        {filtered.map(s => {
          const updateAvail = hasUpdateAvailable(s);
          const isAhead     = aheadOfChannel(s);
          const isSaving    = savingServer === s.id;
          const isTriggering = triggeringServer === s.id;
          return (
            <div key={s.id} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 4, padding: 12, marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <strong>{s.hostname}</strong>
                <ChannelBadge channel={s.channel} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>{s.client_name}</div>
              <div style={{ fontSize: 12, marginBottom: 4, fontFamily: 'var(--mono)' }}>
                v{s.agent_version || '—'}
                {updateAvail && (
                  <span style={{ marginLeft: 6, color: 'var(--yellow)', fontSize: 11 }}>
                    ⚠ {latestByChannel[s.channel]} disponible
                  </span>
                )}
                {isAhead && (
                  <span style={{ marginLeft: 6, color: 'var(--accent)', fontSize: 11 }}>
                    ℹ más nueva que el canal ({latestByChannel[s.channel]})
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
                {formatEnvironment(s.agent_environment)}
              </div>
              {(() => {
                const unmet = serverUnmetRequirements(s);
                return unmet.length > 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 4 }}>
                    ⚠ no cumple requisitos de {latestByChannel[s.channel]}: {unmet.map(u => `${u.key} ${u.actual} < ${u.minVersion}`).join(', ')}
                  </div>
                ) : null;
              })()}
              <PendingUpdateInfo server={s} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={s.auto_update}
                    onChange={() => toggleAutoUpdate(s)}
                    disabled={isSaving}
                  />
                  Auto-update
                </label>
                <button
                  onClick={() => toggleChannel(s)}
                  disabled={isSaving}
                  title={`Cambiar a ${s.channel === 'stable' ? 'beta' : 'stable'}`}
                  style={{
                    fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer',
                    background: s.channel === 'beta' ? 'rgba(96,165,250,0.15)' : 'rgba(74,222,128,0.15)',
                    border: `1px solid ${s.channel === 'beta' ? '#60a5fa' : '#4ade80'}`,
                    color: s.channel === 'beta' ? '#60a5fa' : '#4ade80',
                    padding: '4px 10px', borderRadius: 4, textTransform: 'uppercase',
                  }}
                >
                  {s.channel || '—'}
                </button>
                {updateAvail && isAdmin && (
                  <button
                    onClick={() => triggerUpdate(s)}
                    disabled={isTriggering}
                    style={{ fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'var(--accent)', border: 'none', color: 'var(--bg)', padding: '4px 8px', flex: 1 }}
                  >
                    {isTriggering ? 'Disparando...' : 'Actualizar ahora'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </section>
    );
  }

  return (
    <section>
      <h2 style={{ fontSize: 16, marginBottom: 12 }}>
        Servidores ({filtered.length} de {servers.length})
      </h2>
      {filterRow}
      <div style={{ border: '1px solid var(--border)', borderRadius: 4, maxHeight: 520, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Servidor</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Cliente</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Canal</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Auto-update</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Versión actual</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Entorno</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Pendiente</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Último check</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const updateAvail  = hasUpdateAvailable(s);
              const isAhead      = aheadOfChannel(s);
              const isSaving     = savingServer === s.id;
              const isTriggering = triggeringServer === s.id;
              return (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px' }}><strong>{s.hostname}</strong></td>
                  <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--muted)' }}>{s.client_name}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <button
                      onClick={() => toggleChannel(s)}
                      disabled={isSaving}
                      title={`Cambiar a ${s.channel === 'stable' ? 'beta' : 'stable'}`}
                      style={{
                        fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer',
                        background: s.channel === 'beta' ? 'rgba(96,165,250,0.15)' : 'rgba(74,222,128,0.15)',
                        border: `1px solid ${s.channel === 'beta' ? '#60a5fa' : '#4ade80'}`,
                        color: s.channel === 'beta' ? '#60a5fa' : '#4ade80',
                        padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase',
                      }}
                    >
                      {s.channel || '—'}
                    </button>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={s.auto_update}
                      onChange={() => toggleAutoUpdate(s)}
                      disabled={isSaving}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)', fontSize: 13 }}>
                    {s.agent_version || '—'}
                    {updateAvail && (
                      <span style={{ marginLeft: 8, color: 'var(--yellow)', fontSize: 11 }}>
                        ⚠ {latestByChannel[s.channel]} disponible
                      </span>
                    )}
                    {isAhead && (
                      <span style={{ marginLeft: 8, color: 'var(--accent)', fontSize: 11 }}>
                        ℹ más nueva que el canal ({latestByChannel[s.channel]})
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--muted)' }}>
                    {formatEnvironment(s.agent_environment)}
                    {(() => {
                      const unmet = serverUnmetRequirements(s);
                      return unmet.length > 0 ? (
                        <div style={{ fontSize: 11, color: 'var(--yellow)', marginTop: 2 }} title={unmet.map(u => `${u.key}: requiere ${u.minVersion}, tiene ${u.actual}`).join(' · ')}>
                          ⚠ no cumple requisitos
                        </div>
                      ) : null;
                    })()}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                    {hasPendingUpdate(s) ? <PendingUpdateInfo server={s} compact /> : '—'}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--muted)' }}>
                    {s.last_update_check ? new Date(s.last_update_check).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {updateAvail && isAdmin && (
                      <button
                        onClick={() => triggerUpdate(s)}
                        disabled={isTriggering}
                        style={{ fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'var(--accent)', border: 'none', color: 'var(--bg)', padding: '5px 10px' }}
                      >
                        {isTriggering ? 'Disparando...' : 'Actualizar ahora'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                  No hay servidores con esos filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── UpdatesPage ─────────────────────────────────────────────────────────────

export default function UpdatesPage() {
  const { isAdmin } = useAuth();
  const [releases, setReleases] = useState([]);
  const [servers,  setServers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [toast,    setToast]    = useState({ msg: '', type: '' });

  const [filterChannel,     setFilterChannel]     = useState('all');
  const [filterClient,      setFilterClient]       = useState('all');
  const [filterUpdateAvail, setFilterUpdateAvail]  = useState('all');

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: '' }), 3500);
  };

  async function loadAll() {
    setLoading(true);
    try {
      const [rel, srv] = await Promise.all([
        api.getAgentReleases(),
        api.getServersOverview(),
      ]);
      setReleases(rel);
      setServers(srv);
    } catch (e) {
      showToast(`Error cargando datos: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 13 }}>
      CARGANDO...
    </div>
  );

  return (
    <div>
      <h1 style={{ fontSize: 20, marginBottom: 24, fontFamily: 'var(--mono)', letterSpacing: 1 }}>
        ACTUALIZACIONES DEL AGENTE
      </h1>

      <Toast {...toast} />

      <ReleasesSection
        releases={releases}
        servers={servers}
        onChanged={loadAll}
        showToast={showToast}
        isAdmin={isAdmin}
      />

      <ServersUpdateSection
        servers={servers}
        releases={releases}
        filterChannel={filterChannel}
        setFilterChannel={setFilterChannel}
        filterClient={filterClient}
        setFilterClient={setFilterClient}
        filterUpdateAvail={filterUpdateAvail}
        setFilterUpdateAvail={setFilterUpdateAvail}
        onChanged={loadAll}
        showToast={showToast}
        isAdmin={isAdmin}
      />
    </div>
  );
}
